import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import {
  MediaPlayerProvider,
  useMediaPlayerStore
} from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { BIG_PLAY_BUTTON } from '..';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { PlayButton } from '~/ui/buttons';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import ReactDOM from 'react-dom';
import { drillAnimation } from '~/ui/animation';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { TickMarkIcon } from '~/training/sequence/templates/items/audio-popup/components/TickMarkIcon';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { EditorMediaFile, useEditorStore } from '~/shell/Store';
import NarrationPopup from '~/editor/narration/NarrationPopup';

const DrillBoxAnimation = styled.div`
  width: 120px;
  height: 120px;
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  background-color: accentDark;
  border-radius: 50%;
  opacity: 0.5;
`;

const WrapperForButton = styled.div`
  .progressSvg {
    scale: 2;
    margin-left: 25%;
    margin-top: 25%;
    stroke-width: 1px;
  }
  .tickMark {
    z-index: 1;
  }
`;

const selector = (s: MediaPlayerStore) =>
  select(s, 'loadMedia', 'status', 'toggle', 'progress');

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

interface Props {
  id?: string;
  audioId?: string | number;
  mediaUrl: string;
  subtitlesUrl?: string;
  clearSubitemAudio?: boolean;
}

const Button = ({
  id,
  mediaUrl,
  subtitlesUrl,
  audioId,
  clearSubitemAudio = false
}: Props) => {
  const [showAudioPopup, setShowAudioPopup] = useState(false);
  const { editorMode, updateFile } = useEditorStore();

  const _id = id?.split('_')[0];

  const { loadMedia, status, toggle, progress } = useMediaPlayerStore(
    selector,
    shallow
  );

  const { addBlocker, removeBlocker } = useSequenceStore(seqSelector, shallow);

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    loadMedia(mediaUrl, subtitlesUrl, 'audio', false, 0);
  }, []);

  useEffect(() => {
    addBlocker(mediaUrl);

    if (status == 'nomedia') {
      removeBlocker(mediaUrl);
    }
  }, [status]);

  useEffect(() => {
    if ((mediaUrl && status === 'done') || !mediaUrl) {
      removeBlocker(mediaUrl);
    }
  }, [status, open, mediaUrl]);

  const clickHandler = () => {
    if (editorMode) {
      setShowAudioPopup(true);
    } else {
      if (globalPlayer) {
        globalPlayer.pause();
      }

      toggle();
    }
  };

  const onSelectNarration = (audio: EditorMediaFile) => {
    if (id && audio?.id) {
      updateFile(id.toString(), audio, 'audios');
    }

    setShowAudioPopup(false);
  };

  return (
    <WrapperForButton>
      <x.div
        w="100px"
        h="100px"
        borderRadius="50%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        position="relative"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
        className="audioPlayerButtonDiv"
      >
        <DrillBoxAnimation className="drillBoxAnimation" />
        <AudioPlayer />
        {status === 'done' ? (
          <TickMarkIcon className="tickMark" />
        ) : (
          <PlayButton
            onClick={clickHandler}
            state={status === 'playing' ? 'playing' : 'speaker'}
            progress={progress}
            style={{ width: '100px', height: '100px', borderRadius: '50%' }}
            className="playButton"
          />
        )}
      </x.div>
      {showAudioPopup &&
        editorMode &&
        ReactDOM.createPortal(
          <NarrationPopup
            onClose={() => setShowAudioPopup(false)}
            skipUpdate={true}
            onSelectCallback={onSelectNarration}
            id={audioId?.toString() || _id}
            skipSequenceNarration={true}
            subitemId={_id}
            clearSubitemAudio={clearSubitemAudio}
          />,
          document.body
        )}
    </WrapperForButton>
  );
};

export { Button as AudioPlayerButton };

export type BigPlayButtonDefinition = ItemDefinition<
  typeof BIG_PLAY_BUTTON,
  Props
>;

export class BigPlayBtn extends Item<typeof BIG_PLAY_BUTTON, Props> {
  protected render({
    id,
    mediaUrl,
    subtitlesUrl,
    audioId
  }: RenderProps<Props>) {
    return (
      <MediaPlayerProvider>
        <Button
          id={id}
          mediaUrl={mediaUrl}
          subtitlesUrl={subtitlesUrl}
          audioId={audioId}
        />
        {ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      </MediaPlayerProvider>
    );
  }
}

registry[BIG_PLAY_BUTTON] = BigPlayBtn;
